import { Button, type ButtonProps } from '@trm/ui-kit-admin/v7';
import { cx } from '@trm/ui-kit-admin/v7/utils';
import { forwardRef } from 'react';

/**
 * Props for the OnboardingButton component
 * Extends all Button props from @trm/ui-kit-admin/v7
 */
export interface OnboardingButtonProps extends ButtonProps {
  /**
   * Additional CSS classes to apply to the button
   */
  className?: string;
}

/**
 * Shared button component for onboarding and authentication flows.
 *
 * This component provides consistent styling for primary action buttons across:
 * - Registration forms (AuthRegisterCognitoNew)
 * - Email verification flows (EmailVerification)
 * - Password setup (SetCognitoPassword)
 * - Password reset (ForgotPassword)
 * - Login flows (LoginWithEmail, LoginMFA)
 *
 * ## Design Specifications
 *
 * - **Background**: White (rgb(255, 255, 255))
 * - **Hover State**: Gray-200 - subtle transition from white
 * - **Disabled State**: Gray-300 background with black text
 * - **Text**: Black, semibold weight
 *
 * ## Usage
 *
 * @example
 * Basic usage
 * ```tsx
 * <OnboardingButton type="submit" isLoading={isSubmitting}>
 *   Sign Up For Free
 * </OnboardingButton>
 * ```
 *
 * @example
 * With custom className
 * ```tsx
 * <OnboardingButton className="mt-4" onClick={handleNext}>
 *   Continue
 * </OnboardingButton>
 * ```
 *
 * @example
 * Disabled state
 * ```tsx
 * <OnboardingButton isDisabled={!isValid}>
 *   Submit
 * </OnboardingButton>
 * ```
 */
export const OnboardingButton = forwardRef<HTMLButtonElement, OnboardingButtonProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        className={cx(
          'w-full bg-white p-4 font-semibold text-black',
          'hover:bg-gray-200',
          'disabled:bg-gray-300 disabled:text-black',
          'data-loading:bg-gray-200',
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    );
  },
);

OnboardingButton.displayName = 'OnboardingButton';
